import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Sprout, Loader2, CheckCircle2, XCircle, Clock, ChevronDown, AlertTriangle } from 'lucide-react';
import { demoSaatgut } from '../api';
import type { DemoSaatgut } from '../api';
import { format } from 'date-fns';

/**
 * Demo-Inhalte einer Instanz.
 *
 * Erscheint nur bei Umgebung Demo oder Test — die Entscheidung darueber faellt
 * im Backend, nicht hier: `angeboten: false` kommt mit einer Begruendung, und
 * die wird angezeigt statt die Karte wortlos verschwinden zu lassen. Wer sucht,
 * warum er etwas nicht sieht, soll es lesen koennen.
 */

function Zustand({ lauf }: { lauf: DemoSaatgut['letzterLauf'] }) {
  if (!lauf) return <span className="text-xs text-slate-400">noch nie eingespielt</span>;

  const wann = lauf.finishedAt ?? lauf.startedAt;
  const datum = wann ? format(new Date(wann), 'dd.MM.yy HH:mm') : null;

  if (lauf.status === 'DONE')
    return (
      <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
        <CheckCircle2 size={12} /> eingespielt{datum && ` · ${datum}`}
      </span>
    );
  if (lauf.status === 'FAILED')
    return (
      <span className="inline-flex items-center gap-1 text-xs text-red-600">
        <XCircle size={12} /> fehlgeschlagen{datum && ` · ${datum}`}
      </span>
    );
  if (lauf.status === 'RUNNING')
    return (
      <span className="inline-flex items-center gap-1 text-xs text-brand-600">
        <Loader2 size={12} className="animate-spin" /> läuft
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 text-xs text-amber-700">
      <Clock size={12} /> wartet auf die Bereitstellung
    </span>
  );
}

export default function DemoInhalteKarte({ instanceId }: { instanceId: string }) {
  const qc = useQueryClient();
  const [offen, setOffen] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['demo-saatgut', instanceId],
    queryFn: () => demoSaatgut.fuerInstanz(instanceId),
    // Solange etwas laeuft, nachsehen. Ein Seed dauert Sekunden bis Minuten,
    // und ohne das bliebe „läuft" stehen, bis jemand die Seite neu laedt.
    refetchInterval: (q) =>
      q.state.data?.eintraege.some((e) =>
        e.letzterLauf && ['PENDING', 'RUNNING'].includes(e.letzterLauf.status),
      )
        ? 5000
        : false,
  });

  const einspielen = useMutation({
    mutationFn: (seedId: string) => demoSaatgut.einspielen(instanceId, [seedId]),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['demo-saatgut', instanceId] }),
  });

  if (isLoading) return <div className="h-28 animate-pulse rounded-xl bg-slate-100" />;
  if (!data) return null;

  return (
    <div className="rounded-xl border border-slate-200 bg-surface p-5">
      <div className="flex items-center gap-2">
        <Sprout size={16} className="text-brand-600" />
        <h3 className="text-sm font-semibold text-slate-800">Demo-Inhalte</h3>
      </div>
      <p className="mt-1 text-xs text-slate-400">
        Vorbereitete Inhalte des Produkts, damit eine Vorführung nicht auf einer leeren Instanz
        stattfindet.
      </p>

      {data.grund && (
        <p className="mt-3 rounded-lg bg-slate-50 px-3 py-2 text-xs leading-snug text-slate-500">
          {data.grund}
        </p>
      )}

      {data.eintraege.length > 0 && (
        <div className="mt-4 space-y-2">
          {data.eintraege.map((e) => {
            const laeuft =
              e.letzterLauf && ['PENDING', 'RUNNING'].includes(e.letzterLauf.status);
            return (
              <div key={e.id} className="rounded-lg border border-slate-200 p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-medium text-slate-800">{e.name}</span>
                      {e.zerstoerend && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-1.5 py-0.5 text-[10px] font-medium text-red-700">
                          <AlertTriangle size={10} /> löscht vorhandene Inhalte
                        </span>
                      )}
                    </div>
                    <p className="mt-0.5 text-xs leading-snug text-slate-500">{e.beschreibung}</p>
                    <div className="mt-1.5 flex items-center gap-2">
                      <Zustand lauf={e.letzterLauf} />
                      {e.letzterLauf?.log && (
                        <button
                          type="button"
                          onClick={() => setOffen(offen === e.id ? null : e.id)}
                          className="inline-flex items-center gap-0.5 text-xs text-slate-400 hover:text-slate-600"
                        >
                          Ausgabe
                          <ChevronDown
                            size={11}
                            className={offen === e.id ? 'rotate-180 transition' : 'transition'}
                          />
                        </button>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      // Nachfrage nur bei den zerstoerenden. Eine Rueckfrage vor
                      // jedem Knopf erzieht dazu, sie wegzuklicken — und dann
                      // wirkt sie auch dort nicht, wo sie noetig waere.
                      if (
                        e.zerstoerend &&
                        !confirm(
                          `„${e.name}" löscht in dieser Instanz vorhandene Inhalte, bevor neue angelegt werden. Fortfahren?`,
                        )
                      )
                        return;
                      einspielen.mutate(e.id);
                    }}
                    disabled={Boolean(laeuft) || einspielen.isPending}
                    className="shrink-0 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                  >
                    {e.letzterLauf?.status === 'DONE' ? 'erneut einspielen' : 'einspielen'}
                  </button>
                </div>
                {offen === e.id && e.letzterLauf?.log && (
                  // `overflow-x-auto` am eigenen Behaelter: eine lange Zeile
                  // Skriptausgabe soll nicht die ganze Seite breit schieben.
                  <pre className="mt-2 max-h-52 overflow-auto rounded bg-slate-900 p-2 text-[11px] leading-snug text-slate-200">
                    {e.letzterLauf.log}
                  </pre>
                )}
              </div>
            );
          })}
        </div>
      )}

      {data.angeboten && data.eintraege.length > 0 && (
        <p className="mt-3 text-xs leading-snug text-slate-400">
          Die Seeds laufen im Backend-Container der Instanz und schreiben in deren Datenbank.
          Erneutes Einspielen legt je nach Seed neu an oder ersetzt den vorherigen Stand — was
          genau, steht in der Beschreibung.
        </p>
      )}
    </div>
  );
}
